import { InternalEvent } from "components/Layout/Campaign/Common/CampaignTriggers/InternalEvent/helpers";
import { Nullable } from "../global";
import { CampaignLimits, CampaignLimitUsages } from "./limits";
import { DirectCampaignRule, ReferralCampaignRule } from "./rules";
import { TransactionItem } from "./transactionFilters";
import {
  CampaignTriggers,
  CampaignTriggersUnion,
  TriggerStrategy,
  TriggerStrategyType,
} from "./triggers";
import { CampaignTypes, CampaignTypeUnion } from "./types";

export type CampaignActivity = {
  startsAt: Nullable<string>;
  endsAt: Nullable<string>;
};

export enum VisibilityTarget {
  NONE = "none",
  SEGMENT = "segment",
  TIER = "tier",
  ALL = "all",
}

export type CampaignVisibilityTarget = `${VisibilityTarget}`;

export interface CampaignVisibility {
  target: CampaignVisibilityTarget;
  segments?: string[];
  tiers?: string[];
}

export enum AudienceType {
  EVERYONE = "everyone",
  TIER = "tier",
  SEGMENT = "segment",
}

export type AudienceTypeUnion = `${AudienceType}`;

export type Audience = {
  target: AudienceTypeUnion;
  tiers?: string[];
  segments?: string[];
};

export interface SelectedSchemaCondition {
  attribute: string;
  data: null | undefined | boolean;
  label: string;
  name?: string;
  type: string;
  operator?: string;
}

export type SchemaCondition = {
  active: boolean;
  createdAt: string;
  eventType: string;
  name: string;
  schema: {
    fields: {
      description: string;
      name: string;
      type: string;
    }[];
  };
};

export enum CodeGeneratorCharacterSetType {
  ALPHA = "alpha",
  ALPHANUM = "alphanum",
  NUM = "num",
}

export type CodeGeneratorCharacterSet = `${CodeGeneratorCharacterSetType}`;

export interface CampaignCommonFields {
  translations: {
    name: string;
    description: string;
    locale: string;
    id?: number;
  }[];
  activity: CampaignActivity;
  labels: { key: string; value: string }[];
  limits: CampaignLimits;
  active: boolean;
  displayOrder?: number;
  visibility?: CampaignVisibility;
  limitUsages?: CampaignLimitUsages;
  name?: string;
  campaignId?: string;
}

// Direct type campaigns

export type DirectCampaign = CampaignCommonFields & {
  type: CampaignTypes.DIRECT;
  rules: DirectCampaignRule[];
};

export type DirectTransactionCampaign = DirectCampaign & {
  trigger: CampaignTriggers.TRANSACTION;
  transactionItemsFilters?: TransactionItem[];
};

type DirectReturnTransactionCampaign = DirectCampaign & {
  trigger: CampaignTriggers.RETURN_TRANSACTION;
  transactionItemsFilters?: TransactionItem[];
};

type DirectInternalEventCampaign = DirectCampaign & {
  trigger: CampaignTriggers.INTERNAL_EVENT;
  event: InternalEvent;
};

type DirectCustomEventCampaign = DirectCampaign & {
  trigger: CampaignTriggers.CUSTOM_EVENT;
  event: string;
};

type DirectAchievementCampaign = DirectCampaign & {
  trigger: CampaignTriggers.ACHIEVEMENT;
  achievementId: string;
};

type DirectRedemptionCodeCampaign = DirectCampaign & {
  trigger: CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE;
  generateCodes: number;
  codeGenerator: {
    characterSet: CodeGeneratorCharacterSet;
    length: number;
    prefix?: string;
  };
  eventCodeAttribute: string;
  event: string;
};

export type DirectTimeCampaign = DirectCampaign & {
  trigger: CampaignTriggers.TIME;
  triggerStrategy: { type: TriggerStrategyType.DAILY };
  audience: {
    target: AudienceType.EVERYONE;
  };
};

// Referral type campaigns

export type ReferralCampaign = CampaignCommonFields & {
  type: CampaignTypes.REFERRAL;
  rules: ReferralCampaignRule[];
  multiLevel?: number;
};

export type ReferralTransactionCampaign = ReferralCampaign & {
  trigger: CampaignTriggers.TRANSACTION;
  transactionItemsFilters?: TransactionItem[];
};

type ReferralReturnTransactionCampaign = ReferralCampaign & {
  trigger: CampaignTriggers.RETURN_TRANSACTION;
  transactionItemsFilters?: TransactionItem[];
};

type ReferralInternalEventCampaign = ReferralCampaign & {
  trigger: CampaignTriggers.INTERNAL_EVENT;
  event: InternalEvent;
};

type ReferralCustomEventCampaign = ReferralCampaign & {
  trigger: CampaignTriggers.CUSTOM_EVENT;
  event: string;
};

type ReferralAchievementCampaign = ReferralCampaign & {
  trigger: CampaignTriggers.ACHIEVEMENT;
  achievementId: string;
};

type ReferralRedemptionCodeCampaign = ReferralCampaign & {
  trigger: CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE;
  generateCodes: number;
  codeGenerator: {
    characterSet: CodeGeneratorCharacterSet;
    length: number;
    prefix?: string;
  };
  eventCodeAttribute: string;
  event: string;
};

// Time based campaign
export type TimeBasedCampaign = CampaignCommonFields & {
  type: CampaignTypes.TIME;
  rules: DirectCampaignRule[];
  audience?: Audience;
  trigger: CampaignTriggers.TIME;
  memberFilter?: {
    strategy: "birthday";
  };
  triggerStrategy: TriggerStrategy;
};

export type TransactionCampaigns =
  | DirectTransactionCampaign
  | DirectReturnTransactionCampaign
  | ReferralTransactionCampaign
  | ReferralReturnTransactionCampaign;

export type AchievementCampaigns =
  | ReferralAchievementCampaign
  | DirectAchievementCampaign;

export type EventCampaigns =
  | DirectInternalEventCampaign
  | DirectCustomEventCampaign
  | DirectRedemptionCodeCampaign
  | ReferralInternalEventCampaign
  | ReferralCustomEventCampaign
  | ReferralRedemptionCodeCampaign;

export type CampaignFEValues =
  | TransactionCampaigns
  | AchievementCampaigns
  | EventCampaigns
  | TimeBasedCampaign;

export type CampaignBEData = CampaignFEValues & {
  name: string;
  campaignId: string;
  description?: string;
};

export type VisibleCampaign = {
  campaignId: string;
  name: string;
  description?: string;
  type: CampaignTypeUnion;
  trigger: CampaignTriggersUnion;
  active: boolean;
  activity: CampaignActivity;
  createdAt: string;
  translations: {
    [code: string]: {
      name: string;
      description?: string;
    };
  };
};

type SavedCampaign = {
  campaignId: string;
  data: CampaignFEValues;
};

type UnSavedCampaign = {
  code: number;
  message: string;
  errors: any[];
  data: CampaignFEValues;
};

export type AddCampaignBatchResponse = (SavedCampaign | UnSavedCampaign)[];

export type UnSuccessfulImportedCampaignData = {
  message: string;
  errors: any[];
  data: CampaignFEValues;
  importedId: number;
};

export enum ImportType {
  GLOBAL = "global",
  SINGLE = "single",
}
